import { FC, ReactNode } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  BloqueioPrevia,
  ItemExpurgo,
  ItemPrevia,
  MOTIVO_BLOQUEIO_LABEL,
  MOTIVO_INCLUSAO_LABEL,
  MotivoBloqueio,
  rotuloCategoria,
} from '@/types/privacidade'
import { formatDateTime, formatFileSize } from '@/utils/formatters'

const ehItemExpurgo = (i: ItemPrevia | ItemExpurgo): i is ItemExpurgo => 'status' in i

const ROTULO_STATUS_ITEM: Record<string, string> = {
  PENDENTE: 'Pendente',
  EXECUTANDO: 'Executando',
  AGUARDANDO_COPIAS: 'Aguardando cópias',
  CONCLUIDO: 'Concluído',
  ERRO: 'Erro',
  REJEITADO: 'Rejeitado',
  CANCELADO: 'Cancelado',
  RETIRADO: 'Retirado pelo DPO',
}

/**
 * Itens da prévia ou do expurgo (AN-12 §5.1): o servidor já expandiu a cadeia de versões e as
 * cópias por reuso — a tela só mostra o motivo de inclusão de cada um. Documento expurgado
 * não tem nome (a linha fica mínima): mostra "Arquivo expurgado em …".
 */
export const ItensExpurgoTabela: FC<{
  itens: (ItemPrevia | ItemExpurgo)[]
  /** Módulos que precisam confirmar a remoção das cópias (configuração). */
  confirmacoesEsperadas?: string[]
  /** Ação por item (ex.: "Retirar item" do DPO) — quem decide se aparece é quem chama. */
  acaoItem?: (item: ItemExpurgo) => ReactNode
}> = ({ itens, confirmacoesEsperadas, acaoItem }) => {
  const comStatus = itens.some(ehItemExpurgo)
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label="Itens do expurgo">
        <TableHead>
          <TableRow>
            <TableCell>Documento</TableCell>
            <TableCell>Categoria</TableCell>
            <TableCell>Versão</TableCell>
            <TableCell>Inclusão</TableCell>
            <TableCell>Tamanho</TableCell>
            {comStatus && <TableCell>Situação</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {itens.map((i) => {
            const item = ehItemExpurgo(i) ? i : null
            return (
              <TableRow key={i.documentoId} data-testid={`item-${i.documentoId}`}>
                <TableCell sx={{ maxWidth: 220, wordBreak: 'break-all' }}>
                  {item?.expurgadoEm ? (
                    <Typography variant="body2" color="text.secondary">
                      Arquivo expurgado em {formatDateTime(item.expurgadoEm)}
                    </Typography>
                  ) : (
                    <Typography variant="body2">{i.nome || '—'}</Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {i.documentoId.slice(0, 8)}
                    {i.tipoDocumento ? ` · ${i.tipoDocumento}` : ''}
                  </Typography>
                </TableCell>
                <TableCell>{i.categoria ? rotuloCategoria(i.categoria) : '—'}</TableCell>
                <TableCell>{i.versao != null ? `v${i.versao}` : '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={MOTIVO_INCLUSAO_LABEL[i.motivoInclusao] ?? i.motivoInclusao}
                  />
                </TableCell>
                <TableCell>{i.tamanhoBytes != null ? formatFileSize(i.tamanhoBytes) : '—'}</TableCell>
                {comStatus && (
                  <TableCell>
                    {item ? (
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{ROTULO_STATUS_ITEM[item.status] ?? item.status}</Typography>
                        {(confirmacoesEsperadas ?? []).length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {(confirmacoesEsperadas ?? []).map((m) => {
                              const ok = (item.confirmacoes ?? []).includes(m)
                              return (
                                <Chip
                                  key={m}
                                  size="small"
                                  color={ok ? 'success' : 'default'}
                                  variant={ok ? 'filled' : 'outlined'}
                                  label={`${m}: ${ok ? 'confirmado' : 'pendente'}`}
                                />
                              )
                            })}
                          </Stack>
                        )}
                        {item.ultimoErro && (
                          <Typography variant="caption" color="error">
                            {item.ultimoErro} ({item.tentativas ?? 0} tentativa(s))
                          </Typography>
                        )}
                        {item.status === 'RETIRADO' && item.motivoRetirada && (
                          <Typography variant="caption" color="text.secondary">
                            Retirado em {formatDateTime(item.retiradoEm)}: {item.motivoRetirada}
                          </Typography>
                        )}
                        {acaoItem?.(item)}
                      </Stack>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

/** Bloqueios da prévia: qualquer um recusa o pedido inteiro (409 no servidor). */
export const BloqueiosPrevia: FC<{ bloqueios: BloqueioPrevia[] }> = ({ bloqueios }) =>
  bloqueios.length === 0 ? null : (
    <Alert severity="error" data-testid="bloqueios-previa">
      <AlertTitle>O expurgo não pode ser pedido: {bloqueios.length} bloqueio(s)</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {bloqueios.map((b) => (
          <li key={`${b.documentoId}-${b.codigo}`}>
            <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
              {b.documentoId.slice(0, 8)}
            </Typography>{' '}
            — {MOTIVO_BLOQUEIO_LABEL[b.codigo as MotivoBloqueio] ?? b.codigo}
            {b.detalhe ? ` (${b.detalhe})` : ''}
          </li>
        ))}
      </Box>
    </Alert>
  )
